import {AddCustomItem, AddCustomItemCallback, AddCustomItemContractModel, CustomBagItemModel} from "@next/eventservice"
import logger from "@next/core-logger"
import {ShoppingBagApiPost} from "../../api/shoppingbag"

const addCustomItemESB = new AddCustomItem()
const AddCustomItemCallbackESB = new AddCustomItemCallback()

class SummaryProperty {
    Field: string
    Value: string
}

class OptionNumberAndSizeCodeValuePair {
    OptionNumber: string
    SizeCode: string

    constructor(optionNumber: string, sizeCode: string) {
        this.OptionNumber = optionNumber
        this.SizeCode = sizeCode
    }
}

class AddonsProperty {
    AddonItemNumber = ""
    AddonQuantity = 0
    AddonThumbnailUrl = ""
    AddonType = ""
    AddonPersonalisedMessage = ""
    AddonPrice = 0.0
}

class AddCustomItemApiModel {
    Chain = ""
    CustomItemAddons: AddonsProperty[] = []
    CustomItemDeliveryDetails?: {
        Name?: string
        HouseNumber?: string
        PartialAddressLine1?: string
        AddressLine1?: string
        AddressLine2?: string
        AddressLine3?: string
        AddressLine4?: string
        AddressLine5?: string
        Postcode?: string
        DeliveryDate?: string
        ContactNumber?: string
    }

    OptionNumberAndSizeCodeList: Array<OptionNumberAndSizeCodeValuePair> = []

    SizeCode? = ""
    FulfillmentType = ""
    ItemId = ""
    ItemType = ""
    Mei = ""
    Option = ""
    Price = 0
    PrintJobRef = ""
    Qty = 0
    Summary: SummaryProperty[] = []
}

const getImageUrl = (item: CustomBagItemModel, fulfilmentType: string): string => {
    return fulfilmentType === "F"
        ? `${item.cdnUrl}/common/items/default/default/itemimages/altitembag/${item.sku}.jpg`
        : item.thumbnail_url
}

const SendApiRequest = (url: string, data: AddCustomItemContractModel) => {
    try {
        const apiModel = new AddCustomItemApiModel()

        const itemToAdd = data.items[0]

        apiModel.Chain = itemToAdd.chain // to come from pdp
        apiModel.CustomItemAddons = [] // TODO FLOWERS
        apiModel.FulfillmentType = itemToAdd.fulfillment_type || itemToAdd.fulfilmentType || ""
        apiModel.SizeCode = itemToAdd.option_code
        apiModel.ItemId = itemToAdd.sku
        apiModel.ItemType = itemToAdd.product_type
        apiModel.Mei = itemToAdd.mei
        // MVC repo has this hardcoded to "01" https://dev.azure.com/TFS-Directory/NextOnline/_versionControl?path=$/NextOnline/Dev/UK/Convergence/1)%20Hub/Websites/MVC%20Website/NextMVC3Primary/Scripts/CommonCustomItem.ts&vc.GitRepositoryName=$/NextOnline&_a=contents&version=T
        apiModel.Option = "01"
        apiModel.Qty = itemToAdd.quantity

        apiModel.PrintJobRef = itemToAdd.ref

        // PRICING
        if (typeof itemToAdd.pricing !== "undefined") {
            apiModel.Price = typeof itemToAdd.pricing.unit !== "undefined" ? itemToAdd.pricing.unit : 0.0
        }
        if (typeof itemToAdd.price !== "undefined") {
            apiModel.Price = itemToAdd.price
        }

        // SUMMARY
        const summary: SummaryProperty[] = []

        if (itemToAdd.thumbnail_url) {
            const property = new SummaryProperty()
            property.Field = "image"
            property.Value = itemToAdd.thumbnail_url
            summary.push(property)
        }
        if (typeof itemToAdd.summary !== "undefined") {
            if (Array.isArray(itemToAdd.summary) && itemToAdd.summary.length > 0) {
                itemToAdd.summary.forEach(item => {
                    const property = new SummaryProperty()
                    property.Field = item.name
                    property.Value = item.value
                    summary.push(property)
                })
            } else {
                Object.entries(itemToAdd.summary).forEach(([key, value]) => {
                    const property = new SummaryProperty()
                    property.Field = key
                    property.Value = value.toString()
                    summary.push(property)
                })
            }
        }

        // FLOWERS DETAILS
        apiModel.Summary = summary

        const addons: AddonsProperty[] = []
        if (typeof itemToAdd.addons !== "undefined") {
            if (itemToAdd.addons && itemToAdd.addons.length > 0) {
                itemToAdd.addons.forEach(value => {
                    const addonsProperty = new AddonsProperty()
                    addonsProperty.AddonItemNumber = value.sku
                    addonsProperty.AddonQuantity = value.quantity
                    addonsProperty.AddonThumbnailUrl = getImageUrl(itemToAdd, apiModel.FulfillmentType)
                    addonsProperty.AddonType = value.type
                    addonsProperty.AddonPersonalisedMessage = value.message
                    addonsProperty.AddonPrice = value.price
                    addons.push(addonsProperty)
                })
            }
        }

        apiModel.CustomItemAddons = addons

        if (itemToAdd.fulfilmentType === "F" || itemToAdd.fulfillment_type === "F") {
            apiModel.OptionNumberAndSizeCodeList = [
                {
                    OptionNumber: itemToAdd.option || "",
                    SizeCode: itemToAdd.option_code || "",
                },
            ]
            apiModel.Option = itemToAdd.option || ""
        }

        // DELIVERY DETAILS
        apiModel.CustomItemDeliveryDetails = {}
        if (typeof itemToAdd.delivery_details !== "undefined") {
            apiModel.CustomItemDeliveryDetails.Name =
                typeof itemToAdd.delivery_details.name !== "undefined" ? itemToAdd.delivery_details.name : ""
            apiModel.CustomItemDeliveryDetails.HouseNumber =
                typeof itemToAdd.delivery_details.house_number !== "undefined"
                    ? itemToAdd.delivery_details.house_number
                    : ""
            apiModel.CustomItemDeliveryDetails.PartialAddressLine1 =
                typeof itemToAdd.delivery_details.address_line_1 !== "undefined"
                    ? itemToAdd.delivery_details.address_line_1
                    : ""
            apiModel.CustomItemDeliveryDetails.AddressLine1 = `${apiModel.CustomItemDeliveryDetails.HouseNumber} ${apiModel.CustomItemDeliveryDetails.PartialAddressLine1}`
            apiModel.CustomItemDeliveryDetails.AddressLine2 =
                typeof itemToAdd.delivery_details.address_line_2 !== "undefined"
                    ? itemToAdd.delivery_details.address_line_2
                    : ""
            apiModel.CustomItemDeliveryDetails.AddressLine3 =
                typeof itemToAdd.delivery_details.town !== "undefined" ? itemToAdd.delivery_details.town : ""
            apiModel.CustomItemDeliveryDetails.AddressLine4 =
                typeof itemToAdd.delivery_details.county !== "undefined" ? itemToAdd.delivery_details.county : ""
            apiModel.CustomItemDeliveryDetails.AddressLine5 =
                typeof itemToAdd.delivery_details.country !== "undefined" ? itemToAdd.delivery_details.country : ""
            apiModel.CustomItemDeliveryDetails.Postcode =
                typeof itemToAdd.delivery_details.postcode !== "undefined" ? itemToAdd.delivery_details.postcode : ""
            apiModel.CustomItemDeliveryDetails.DeliveryDate =
                typeof itemToAdd.delivery_details.date !== "undefined" ? itemToAdd.delivery_details.date : ""
            apiModel.CustomItemDeliveryDetails.ContactNumber =
                typeof itemToAdd.delivery_details.contact_number !== "undefined"
                    ? itemToAdd.delivery_details.contact_number
                    : ""
        }

        ShoppingBagApiPost(apiModel, `${url}/addCustomItemToBag`, AddCustomItemCallbackESB, data.eventId, "payload")
    } catch (e) {
        logger.error(e)
    }
}

export const SubscribeToShoppingBagAddCustomItem = (siteUrl: string) => {
    addCustomItemESB.subscribe(data => {
        SendApiRequest(siteUrl, data)
    })
}
